import React, { useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useToast } from '../../context/ToastContext';
import { usd, shortAddr, fmtDate } from '../../utils/format';
import { PACKAGES } from '../../config/contracts';

// Look up any member by ID or wallet address and show their full profile.
export default function UserLookup() {
  const { evittaRead } = useContracts();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const lookup = async () => {
    if (!evittaRead) return;
    const raw = q.trim();
    if (!raw) {
      toast.error('Enter a user ID or wallet address.');
      return;
    }
    setLoading(true);
    setInfo(null);
    setNotFound(false);
    try {
      let userId;
      if (ethers.utils.isAddress(raw)) {
        userId = Number(await evittaRead.addressToId(ethers.utils.getAddress(raw)));
      } else if (/^\d+$/.test(raw)) {
        userId = Number(raw);
      } else {
        toast.error('Enter a valid user ID or 0x address.');
        setLoading(false);
        return;
      }

      if (!userId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const [user, packages, income, elig, details, referrals] = await Promise.all([
        evittaRead.users(userId).catch(() => null),
        evittaRead.getUserPackages(userId).catch(() => []),
        evittaRead.getUserIncomeBreakdown(userId).catch(() => null),
        evittaRead.getUserPoolEligibilities(userId).catch(() => null),
        evittaRead.getMatrixDetails(userId, 1).catch(() => null),
        evittaRead.getUserReferrals(userId).catch(() => []),
      ]);

      if (!user || Number(user.id) === 0) {
        setNotFound(true);
      } else {
        setInfo({
          userId,
          user,
          packages: (packages || []).map((b) => !!b),
          income,
          elig,
          teamSize: details ? Number(details.totalTeamSize) : 0,
          referrals: (referrals || []).map((id) => Number(id)),
        });
      }
    } catch {
      toast.error('Lookup failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{ fontWeight: 700, fontSize: 14.5 }}>User Lookup</div>
        <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>Search by member ID or wallet address.</div>
        <div className="field">
          <input
            placeholder="1234  or  0x…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <button className="btn blue" onClick={lookup} disabled={loading}>
            {loading ? <span className="spin-inline light" /> : null}
            {loading ? 'Searching…' : 'Look Up'}
          </button>
        </div>
      </div>

      {notFound && <div className="card" style={{ marginTop: 12 }}><div className="empty">No member found for that ID / address.</div></div>}

      {info && (
        <div className="card glow fade-in" style={{ marginTop: 12 }}>
          <div className="card-row">
            <div>
              <div style={{ fontWeight: 800, fontSize: 18 }}>Member #{info.userId}</div>
              <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>{shortAddr(info.user.account)}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {info.user.isActive ? <span className="pill ok">Active</span> : <span className="pill off">Inactive</span>}
              {info.user.isVirtual && <span className="pill blue">Virtual</span>}
            </div>
          </div>

          <div className="grid-2" style={{ marginTop: 14 }}>
            <KV label="Upline" value={`#${Number(info.user.referrer) || '—'}`} />
            <KV label="Direct Refs" value={Number(info.user.totalDirectReferrals)} />
            <KV label="Total Team" value={info.teamSize} />
            <KV label="Joined" value={fmtDate(info.user.joinTime)} />
          </div>

          {/* Active packages */}
          <div className="dim" style={{ fontSize: 11, margin: '14px 0 6px' }}>ACTIVE PACKAGES</div>
          <div className="pkg-dots">
            {PACKAGES.map((p, i) => (
              <span key={p.id} className={`pkg-dot ${info.packages[i] ? 'on' : ''}`} title={`$${p.price}`}>{p.id}</span>
            ))}
          </div>

          {/* Direct referrals */}
          <div className="dim" style={{ fontSize: 11, margin: '16px 0 6px' }}>
            DIRECT REFERRALS ({info.referrals.length})
          </div>
          {info.referrals.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {info.referrals.map((id) => (
                <span key={id} className="pill gold">#{id}</span>
              ))}
            </div>
          ) : (
            <span className="dim" style={{ fontSize: 12 }}>No direct referrals.</span>
          )}

          {/* Income */}
          {info.income && (
            <>
              <div className="dim" style={{ fontSize: 11, margin: '16px 0 4px' }}>INCOME</div>
              <Row label="Total Income" value={usd(info.income.totalIncome)} />
              <Row label="Claimable" value={usd(info.income.claimableAmount)} />
              <Row label="Total Withdrawn" value={usd(info.income.totalClaimed)} />
              <Row label="Sponsor" value={usd(info.income.sponsorIncome)} />
              <Row label="Level" value={usd(info.income.levelIncome)} last />
            </>
          )}

          {/* Pool eligibility */}
          {info.elig && (
            <>
              <div className="dim" style={{ fontSize: 11, margin: '16px 0 6px' }}>POOL ELIGIBILITY</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className={`pill ${info.elig.silverEligible ? 'ok' : 'off'}`}>Silver</span>
                <span className={`pill ${info.elig.goldEligible ? 'ok' : 'off'}`}>Gold</span>
                <span className={`pill ${info.elig.diamondEligible ? 'ok' : 'off'}`}>Diamond</span>
                <span className={`pill ${info.elig.specialEligible ? 'ok' : 'off'}`}>Special</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="stat-label">{label}</div>
      <div style={{ fontWeight: 700, fontSize: 15, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Row({ label, value, last }) {
  return (
    <div className="card-row" style={{ padding: '9px 0', borderBottom: last ? 'none' : '1px solid rgba(255,255,255,0.05)' }}>
      <span className="muted" style={{ fontSize: 13 }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: 13.5 }}>{value}</span>
    </div>
  );
}
